"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { createMascot, type ClothLabel, type MascotEngine } from "./engine";
import { setMascot } from "./registry";

const INK = "#111212";

/**
 * Mounted once in the root layout, so A survives every page change. The
 * markup is inert until the engine starts; without JavaScript the logos
 * simply keep their painted yellow A.
 */
export function Mascot({ labels }: { labels: Record<string, ClothLabel> }) {
  const router = useRouter();
  const pathname = usePathname();
  const routerRef = useRef(router);
  const labelsRef = useRef(labels);
  const engineRef = useRef<MascotEngine | null>(null);

  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  useEffect(() => {
    const engine = createMascot({
      push: (href) => routerRef.current.push(href),
      labels: labelsRef.current,
    });
    engineRef.current = engine;
    setMascot(engine);
    engine.start();
    return () => {
      setMascot(null);
      engineRef.current = null;
      engine.destroy();
    };
  }, []);

  useEffect(() => {
    engineRef.current?.routeChanged(pathname);
  }, [pathname]);

  return (
    <>
      {/* the page transition: a cloth A drags across the screen */}
      <div id="cloth" aria-hidden="true" hidden>
        <p className="cl-kick" id="clothKick" />
        <p className="cl-title" id="clothTitle" />
        <div className="cl-floor" />
      </div>

      {/* Catch A: a click layer over the page, the clock, and A's grey twin */}
      <div id="gField" hidden />
      <div id="gHud" aria-live="polite" hidden>
        <i id="gBar" />
        <p id="gScore" />
      </div>
      <svg id="aTwin" viewBox="-34 -84 64 92" aria-hidden="true">
        <g stroke={INK} strokeWidth="2.6" strokeLinecap="round" fill="none">
          <line x1="-6.6" y1="-9" x2="-6.6" y2="0" />
          <line x1="6.6" y1="-9" x2="6.6" y2="0" />
        </g>
        <path d="M-15,-14 L15,-14 L-2.4,-72 L-32.4,-72 Z" fill="#b9bdb9" stroke="#b9bdb9" strokeWidth="10" strokeLinejoin="round" />
        <g stroke={INK} strokeWidth="1.9" strokeLinecap="round">
          <path d="M-21,-57 l4,4 m0,-4 l-4,4 M-7,-57 l4,4 m0,-4 l-4,4" />
          <path d="M-15,-44 H-6" />
        </g>
      </svg>

      <svg id="aLayer" aria-hidden="true">
        <g id="A">
          <g id="aLegs" stroke={INK} strokeWidth="2.6" strokeLinecap="round" fill="none">
            <line id="legL" />
            <line id="legR" />
            <line id="footL" />
            <line id="footR" />
          </g>
          <path id="aBody" fill="#ffd600" stroke="#ffd600" strokeLinejoin="round" />
          <g id="aChest" />
          <g id="aFace">
            <ellipse id="blushL" rx="3.4" ry="2" fill="#ff9d5c" opacity="0" />
            <ellipse id="blushR" rx="3.4" ry="2" fill="#ff9d5c" opacity="0" />
            <g id="eyeL">
              <ellipse className="eo" rx="3.1" ry="4.3" fill={INK} />
              <path className="ep" fill="none" stroke={INK} strokeWidth="1.9" strokeLinecap="round" />
            </g>
            <g id="eyeR">
              <ellipse className="eo" rx="3.1" ry="4.3" fill={INK} />
              <path className="ep" fill="none" stroke={INK} strokeWidth="1.9" strokeLinecap="round" />
            </g>
            <g id="aMouth" />
          </g>
          <g id="aArms" stroke={INK} strokeWidth="2.6" strokeLinecap="round">
            <line id="armL" />
            <line id="armR" />
            <circle id="handL" r="2.2" fill={INK} stroke="none" />
            <circle id="handR" r="2.2" fill={INK} stroke="none" />
          </g>
          <g id="aHand" />
          <g id="aHead" />
        </g>
      </svg>
      <div
        id="aHit"
        role="button"
        tabIndex={0}
        aria-label="A, Yassine's assistant. Press to ask something. You can also drag it."
      />
      <div className="bubble" id="bubble" role="status" aria-live="polite" hidden>
        <p className="who">
          <i>A</i>A · assistant
        </p>
        <p className="b-text" id="bText" />
        <div className="b-acts" id="bActs" />
      </div>
      <div className="zzz" id="zzz" aria-hidden="true">
        <span>z</span>
        <span>z</span>
        <span>z</span>
      </div>
    </>
  );
}
